import { useEffect, useState } from 'react';
import { partners as partnersApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Modal, Can } from '../components/Common';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const BLANK = {
  name: '', mobile: '', password: '', age: '', address: '', email: '',
  shiftStart: '09:00', shiftEnd: '18:00', hoursPerDay: 8, workingDays: [1, 2, 3, 4, 5, 6],
  employmentType: 'full-time', monthlySalary: '', commissionPerOrder: '', commissionPercent: 0,
  hasIncentive: false, incentivePerOrder: '',
  vehicleType: '', vehicleNumber: '',
  aadhaarNumber: '', drivingLicenseNumber: '',
  store: '',
};

/**
 * Creates or edits a delivery partner account.
 *
 * Both the Super Admin and a store manager can create riders; a store-scoped
 * admin has their own store filled in and locked. Documents are optional -
 * whoever creates the account has already met the rider - so the account is
 * usable straight away.
 */
export default function PartnerForm({ partner, storeList, onClose, onSaved }) {
  const toast = useToast();
  const { isSuperAdmin, storeId } = useAuth();

  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!partner) return;
    if (!partner._id) {
      setForm({ ...BLANK, store: isSuperAdmin ? '' : storeId || '' });
      return;
    }
    setForm({
      name: partner.name || '',
      mobile: partner.mobile || '',
      password: '',
      age: partner.age ?? '',
      address: partner.address || '',
      email: partner.email || '',
      shiftStart: partner.shift?.startTime || '09:00',
      shiftEnd: partner.shift?.endTime || '18:00',
      hoursPerDay: partner.shift?.hoursPerDay ?? 8,
      workingDays: partner.shift?.workingDays || [1, 2, 3, 4, 5, 6],
      employmentType: partner.employmentType || 'full-time',
      monthlySalary: partner.monthlySalary ?? '',
      commissionPerOrder: partner.commissionPerOrder ?? '',
      commissionPercent: partner.commissionPercent ?? 0,
      hasIncentive: Boolean(partner.hasIncentive),
      incentivePerOrder: partner.incentivePerOrder ?? '',
      vehicleType: partner.vehicle?.type || '',
      vehicleNumber: partner.vehicle?.number || '',
      aadhaarNumber: partner.kyc?.aadhaarNumber || '',
      drivingLicenseNumber: partner.kyc?.drivingLicenseNumber || '',
      store: partner.store?._id || partner.store || '',
    });
  }, [partner, isSuperAdmin, storeId]);

  if (!partner) return null;
  const isNew = !partner._id;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const toggleDay = (index) =>
    setForm((f) => ({
      ...f,
      workingDays: f.workingDays.includes(index)
        ? f.workingDays.filter((d) => d !== index)
        : [...f.workingDays, index].sort(),
    }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Enter the partner’s name');
    if (!form.mobile.trim()) return toast.error('Enter a mobile number');
    if (isNew && form.password.length < 6) {
      return toast.error('Set a password of at least 6 characters');
    }

    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        age: form.age === '' ? null : Number(form.age),
        address: form.address,
        store: form.store || null,
        shift: {
          startTime: form.shiftStart,
          endTime: form.shiftEnd,
          hoursPerDay: Number(form.hoursPerDay) || 8,
          workingDays: form.workingDays,
        },
        employmentType: form.employmentType,
        monthlySalary: Number(form.monthlySalary) || 0,
        commissionPerOrder: Number(form.commissionPerOrder) || 0,
        commissionPercent: Number(form.commissionPercent) || 0,
        hasIncentive: form.hasIncentive,
        incentivePerOrder: form.hasIncentive ? Number(form.incentivePerOrder) || 0 : 0,
        vehicle: { type: form.vehicleType, number: form.vehicleNumber },
        kyc: { aadhaarNumber: form.aadhaarNumber, drivingLicenseNumber: form.drivingLicenseNumber },
      };
      if (isNew) body.password = form.password;

      if (isNew) await partnersApi.create(body);
      else await partnersApi.update(partner._id, body);

      toast.success(isNew ? 'Delivery partner created' : 'Partner updated');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      wide
      title={isNew ? 'New delivery partner' : `Edit — ${partner.name}`}
      onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : isNew ? 'Create partner' : 'Save changes'}
        </button>
      </>}
    >
      <SectionTitle>Personal details</SectionTitle>
      <div className="row">
        <div className="field"><label>Full name *</label><input className="input" value={form.name} onChange={set('name')} /></div>
        <div className="field"><label>Age</label><input className="input" type="number" value={form.age} onChange={set('age')} /></div>
      </div>
      <div className="row">
        <div className="field">
          <label>Mobile number *</label>
          <input className="input" value={form.mobile} disabled={!isNew} onChange={set('mobile')} placeholder="9876543210" />
          {!isNew ? <div className="hint">The mobile number is the login and cannot be changed.</div> : null}
        </div>
        <div className="field"><label>Email</label><input className="input" type="email" value={form.email} onChange={set('email')} /></div>
      </div>
      <div className="field">
        <label>Address</label>
        <input className="input" value={form.address} onChange={set('address')} placeholder="House, street, area, city" />
      </div>

      {isNew ? (
        <>
          <SectionTitle>Login</SectionTitle>
          <div className="field">
            <label>Password *</label>
            <input className="input" value={form.password} onChange={set('password')} placeholder="At least 6 characters" />
            <div className="hint">
              The partner signs in with their mobile number and this password. They can change it
              in the app, and you can always read or reset it from the partner’s row here.
            </div>
          </div>
        </>
      ) : null}

      <SectionTitle>Shift</SectionTitle>
      <div className="row">
        <div className="field"><label>Starts</label><input className="input" type="time" value={form.shiftStart} onChange={set('shiftStart')} /></div>
        <div className="field"><label>Ends</label><input className="input" type="time" value={form.shiftEnd} onChange={set('shiftEnd')} /></div>
        <div className="field"><label>Hours per day</label><input className="input" type="number" value={form.hoursPerDay} onChange={set('hoursPerDay')} /></div>
      </div>
      <div className="field">
        <label>Working days</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {WEEKDAYS.map((day, index) => {
            const on = form.workingDays.includes(index);
            return (
              <label key={day} className={`perm-chip ${on ? 'on' : ''}`}>
                <input type="checkbox" checked={on} onChange={() => toggleDay(index)} />
                {on ? '✓' : '＋'} {day}
              </label>
            );
          })}
        </div>
      </div>

      <SectionTitle>Pay</SectionTitle>
      <div className="row">
        <div className="field">
          <label>Employment type</label>
          <select className="select" value={form.employmentType} onChange={set('employmentType')}>
            <option value="full-time">Full-time (salaried)</option>
            <option value="commission">Commission per order</option>
          </select>
        </div>
        {form.employmentType === 'full-time' ? (
          <div className="field">
            <label>Monthly salary</label>
            <input className="input" type="number" value={form.monthlySalary} onChange={set('monthlySalary')} />
          </div>
        ) : (
          <>
            <div className="field">
              <label>Per order</label>
              <input className="input" type="number" value={form.commissionPerOrder} onChange={set('commissionPerOrder')} />
            </div>
            <div className="field">
              <label>% of order value</label>
              <input className="input" type="number" value={form.commissionPercent} onChange={set('commissionPercent')} />
            </div>
          </>
        )}
      </div>

      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.hasIncentive} onChange={set('hasIncentive')} />
          <span className="track" /><span>Pay an incentive per delivered order</span>
        </label>
      </div>
      {form.hasIncentive ? (
        <div className="field">
          <label>Incentive per order</label>
          <input className="input" type="number" value={form.incentivePerOrder} onChange={set('incentivePerOrder')} style={{ maxWidth: 180 }} />
          <div className="hint">
            Paid on top of salary or commission, credited automatically on each delivery.
          </div>
        </div>
      ) : null}

      <SectionTitle>Vehicle &amp; documents <span className="muted" style={{ fontWeight: 400 }}>— optional</span></SectionTitle>
      <div className="row">
        <div className="field">
          <label>Vehicle type</label>
          <select className="select" value={form.vehicleType} onChange={set('vehicleType')}>
            <option value="">Not set</option>
            {['bike', 'scooter', 'cycle', 'van'].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Vehicle number</label>
          <input className="input" value={form.vehicleNumber} onChange={set('vehicleNumber')} placeholder="DL8SAB1234" />
        </div>
      </div>
      <div className="row">
        <div className="field"><label>Aadhaar number</label><input className="input" value={form.aadhaarNumber} onChange={set('aadhaarNumber')} /></div>
        <div className="field"><label>Driving licence number</label><input className="input" value={form.drivingLicenseNumber} onChange={set('drivingLicenseNumber')} /></div>
      </div>
      <div className="hint" style={{ marginTop: -6 }}>
        Documents can be added later — the partner can also upload photos of them from the app.
      </div>

      {isSuperAdmin && storeList.length ? (
        <>
          <SectionTitle>Store</SectionTitle>
          <div className="field">
            <select className="select" value={form.store} onChange={set('store')}>
              <option value="">Unassigned</option>
              {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px',
        color: 'var(--muted)', margin: '20px 0 12px', paddingBottom: 6,
        borderBottom: '1px solid var(--line)',
      }}
    >
      {children}
    </div>
  );
}

/** Reads back or resets a rider's password. */
export function CredentialsModal({ partner, onClose }) {
  const toast = useToast();
  const [creds, setCreds] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!partner) return;
    setNewPassword('');
    partnersApi
      .credentials(partner._id)
      .then((res) => setCreds(res.data))
      .catch((err) => toast.error('Could not load credentials', err.message));
  }, [partner, toast]);

  if (!partner) return null;

  const reset = async () => {
    if (newPassword.length < 6) return toast.error('Use at least 6 characters');
    setBusy(true);
    try {
      await partnersApi.setPassword(partner._id, { password: newPassword });
      toast.success('Password updated');
      setCreds((c) => ({ ...c, password: newPassword, hasPassword: true }));
      setNewPassword('');
    } catch (err) {
      toast.error('Could not update the password', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={`Login — ${partner.name}`} onClose={onClose}>
      <div className="field">
        <label>Mobile number (username)</label>
        <input className="input mono" readOnly value={creds?.mobile || partner.mobile} onFocus={(e) => e.target.select()} />
      </div>

      <div className="field">
        <label>Current password</label>
        <input
          className="input mono"
          readOnly
          value={creds ? (creds.password || '— not set —') : 'Loading…'}
          onFocus={(e) => e.target.select()}
          style={{ fontSize: 16, letterSpacing: 1 }}
        />
        <div className="hint">
          Read this out to the partner if they have forgotten it. It stays in step with any change
          they make from the app.
        </div>
      </div>

      <div className="field">
        <label>Set a new password</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 6 characters"
          />
          <Can permission="partner.update">
            <button className="btn btn-primary" onClick={reset} disabled={busy}>
              {busy ? 'Saving…' : 'Reset'}
            </button>
          </Can>
        </div>
      </div>
    </Modal>
  );
}
